/**
 * One status vocabulary for every health indicator in the portal.
 *
 * Keys map 1:1 onto the `--app-status-*` design tokens in
 * `assets/layout/variables/_common.scss`, so a badge, a dot and a card edge
 * all draw the same colour for the same state in light and dark mode.
 */
export type StatusKey = 'online' | 'offline' | 'warning' | 'missing' | 'pending' | 'expired' | 'unknown';

/** PrimeNG Tag/Badge severity, kept so legacy `p-tag` call sites can be migrated gradually. */
export type StatusSeverity = 'success' | 'danger' | 'warn' | 'secondary' | 'info' | 'contrast';

export interface StatusDescriptor {
    key: StatusKey;
    /** Human label, already resolved for the entity type (e.g. "Awaiting Adoption"). */
    label: string;
    severity: StatusSeverity;
    /** Icon class (PrimeIcons or Font Awesome). Always shown next to colour so meaning survives colour-blindness. */
    icon: string;
    /** Display ordering: lower sorts first in "attention" lists. */
    sortOrder: number;
    /** Roll-up ordering: higher wins in `worstOf()`. */
    severityRank: number;
}

const BASE: Record<StatusKey, Omit<StatusDescriptor, 'label'>> = {
    online: { key: 'online', severity: 'success', icon: 'pi pi-check-circle', sortOrder: 0, severityRank: 0 },
    offline: { key: 'offline', severity: 'danger', icon: 'pi pi-times-circle', sortOrder: 1, severityRank: 6 },
    warning: { key: 'warning', severity: 'warn', icon: 'pi pi-exclamation-triangle', sortOrder: 2, severityRank: 5 },
    missing: { key: 'missing', severity: 'secondary', icon: 'pi pi-question-circle', sortOrder: 3, severityRank: 2 },
    pending: { key: 'pending', severity: 'info', icon: 'pi pi-clock', sortOrder: 4, severityRank: 3 },
    expired: { key: 'expired', severity: 'warn', icon: 'pi pi-hourglass', sortOrder: 5, severityRank: 4 },
    unknown: { key: 'unknown', severity: 'secondary', icon: 'pi pi-minus-circle', sortOrder: 6, severityRank: 1 }
};

const DEFAULT_LABEL: Record<StatusKey, string> = {
    online: 'Online',
    offline: 'Offline',
    warning: 'Warning',
    missing: 'Missing',
    pending: 'Pending',
    expired: 'Expired',
    unknown: 'Unknown'
};

/** Build a descriptor for a key, optionally overriding the label. */
export function status(key: StatusKey, label?: string): StatusDescriptor {
    return { ...BASE[key], label: label ?? DEFAULT_LABEL[key] };
}

/** The most severe of a set of statuses; `online` when the set is empty. */
export function worstOf(statuses: Iterable<StatusDescriptor>): StatusDescriptor {
    let worst: StatusDescriptor | null = null;
    for (const s of statuses) {
        if (!worst || s.severityRank > worst.severityRank) worst = s;
    }
    return worst ?? status('online');
}

/** Comparator for "needs attention first" lists. */
export function compareStatus(a: StatusDescriptor, b: StatusDescriptor): number {
    return a.sortOrder - b.sortOrder;
}
